<template>
  <div
    class="klient-item first-analysis-client"
    :class="{
      'custom-client': !isFirstAnalysisClient,
      'admin-client-item': role === 'alex_admin'
    }"
  >
    <div class="klient-item-to-part">
      <div class="first-analysis-identity">
        <div class="client-badges">
          <a
            v-if="parsedSource.href || parsedSource.className === 'source-ads'"
            class="source-badge"
            :class="parsedSource.className"
            :href="parsedSource.href"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ parsedSource.label }}
          </a>
          <client-district-badges :client="clientData" />
        </div>
        <div class="first-analysis-identity-row">
          <div v-if="role === 'alex_admin'" class="assigned-broker">
            <span class="identity-kicker">Finančník</span>
            <div class="assigned-broker-avatar">
              <broker-picker-handler
                :rules="['priority']"
                :broker-id="clientData.financnik"
                :client-info="clientData"
                clients
                klient
              />
            </div>
          </div>
          <div class="client-copy">
            <span v-if="role === 'alex_admin'" class="identity-kicker">Klient</span>
            <user-manager-handler
              :rules="['priority']"
              :client-info="clientData"
              client
              clients
              telephone
            />
          </div>
        </div>
      </div>

      <div
        v-if="isFirstAnalysisClient"
        class="first-analysis-progress"
        aria-label="Postup v Prvej analýze"
      >
        <div class="first-analysis-progress-copy">
          <span>Prvá analýza</span>
          <strong>{{ currentProgressLabel }}</strong>
        </div>
        <div class="first-analysis-bars" aria-hidden="true">
          <span
            v-for="n in 4"
            :key="n"
            :class="{'is-complete': n <= checkPoints}"
          ></span>
        </div>
        <div class="first-analysis-step">{{ checkPoints }}/4 krokov</div>
      </div>

      <div v-else class="custom-client-status">
        <span>Typ klienta</span>
        <strong>Založený brokerom</strong>
        <small>Bez online prvej analýzy</small>
      </div>

      <div
        class="first-analysis-actions"
        :class="{'custom-client-actions': !isFirstAnalysisClient}"
      >
        <div v-if="!isFirstAnalysisClient" class="custom-client-action-copy">
          <span>Dostupné akcie</span>
          <strong>Správa klienta</strong>
        </div>

        <div class="custom-client-action-buttons">
          <div class="first-analysis-action">
            <client-chat-handler :client="clientData" :rules="['single']"/>
          </div>
          <div class="first-analysis-action">
            <mortgage-picker-handler :client="clientData" :rules="['single']"/>
          </div>
        </div>
      </div>
    </div>
    <div class="lastmessage" v-if="clientData.sent_latest_message" @click="openClientChatPopUp">
      <div class="badge" v-if="clientData.sent_unread_messages">
        <div class="text-block-13">
          {{ clientData.sent_unread_messages }}
        </div>
      </div>
      <div>
        {{ clientData.sent_latest_message }}
      </div>
    </div>
  </div>
</template>

<script>
import ClientDistrictBadges from "./ClientDistrictBadges.vue";
import clientSource from "../../../utils/clientSource";
import BrokerPickerHandler from "../../shared/PopupHandler/Popups/BrokerPicker/BrokerPickerHandler";
import UserManagerHandler from "../../shared/PopupHandler/Popups/UserManager/UserManagerHandler";
import MortgagePickerHandler from "../../shared/PopupHandler/Popups/MortgagePicker/MortgagePickerHandler";
import ClientChatHandler from "../../shared/PopupHandler/Popups/ClientChat/ClientChatHandler";
import translateFromDBValue from "../../../mixins/translateFromDBValue";
import {mapGetters} from "vuex";
import {getClientById} from "../../../services/brokerService";

export default {
  name: "NewKlientItem",
  components: {
    ClientDistrictBadges,
    ClientChatHandler,
    MortgagePickerHandler,
    UserManagerHandler,
    BrokerPickerHandler
  },
  props: {
    client: Object,
    search: Boolean,
  },
  computed: {
    ...mapGetters({
      refreshGetter: "popupManagerState/getUserManagerRefresh"
    }),
    firstAnalysisMortgage() {
      const mortgages = this.clientData?.mortgages || [];
      return mortgages.find((mortgage) => (
        mortgage.poznamka_k_prvej_analyze ||
        mortgage.prva_analyza_ciel_dt ||
        mortgage.prva_analyza_pridany_prijem_dt ||
        mortgage.prva_analyza_vypocitane_dti_dt ||
        mortgage.prva_analyza_registracia_dt
      )) || null;
    },
    isFirstAnalysisClient() {
      return Boolean(this.firstAnalysisMortgage);
    },
    currentProgressLabel() {
      if (this.checkPoints === 0) return "Čaká na zadanie účelu";
      if (this.checkPoints >= 4) return "Analýza dokončená";
      return this.progressBarData[this.checkPoints - 1]?.label || "Rozpracované";
    },
    parsedSource() {
      return clientSource(this.clientData);
    }
  },
  mixins: [
    translateFromDBValue,
  ],
  data: () => ({
    checkPoints: 0,
    progressBarData: [],
    clientData: null,
    role: null,
    open: false,
    broker: null,
    origin: null,
  }),
  created () {
    this.clientData = this.client;
    this.role = this.$store.getters["userState/getRole"];
    this.checkProgressBar();
  },
  methods: {
    checkProgressBar() {
      this.checkPoints = 0;
      this.progressBarData = [];
      this.origin = null;
      const mortgage = this.firstAnalysisMortgage;
      if (!mortgage) return;
      const source = mortgage.poznamka_k_prvej_analyze;
      this.origin = source;
      if (mortgage.prva_analyza_ciel_dt) {
        const date = new Date(mortgage.prva_analyza_ciel_dt);
        this.checkPoints++;
        this.progressBarData.push({
          label: `${this.getMortgagePurpose().toUpperCase()}`,
          data:`${date.getUTCDate()}.${date.getUTCMonth() + 1}.${date.getUTCFullYear()}`,
        });
      } else {
        this.progressBarData.push({
          label: "účel",
          data: null,
        });
      }
      if (mortgage.prva_analyza_pridany_prijem_dt) {
        const date = new Date(mortgage.prva_analyza_pridany_prijem_dt);
        this.checkPoints++;
        this.progressBarData.push({
          label: `${this.mapIncomeType(this.clientData.incomes).toUpperCase()}`,
          data:`${date.getUTCDate()}.${date.getUTCMonth() + 1}.${date.getUTCFullYear()}`,
        });
      } else {
        this.progressBarData.push({
          label: "príjem",
          data: null
        });
      }
      if (mortgage.prva_analyza_vypocitane_dti_dt) {
        const mortgageSum = Number(mortgage.suma_nadobudnutie || 0) + Number(mortgage.suma_refinancovanie || 0) + Number(mortgage.suma_bezucel || 0);
        const date = new Date(mortgage.prva_analyza_vypocitane_dti_dt);
        this.checkPoints++;
        this.progressBarData.push({
          label: `SUMA ${mortgageSum ? mortgageSum : 0}€`,
          data:`${date.getUTCDate()}.${date.getUTCMonth() + 1}.${date.getUTCFullYear()}`,
        });
      } else {
        this.progressBarData.push({
          label: "DTI",
          data: null
        });
      }
      if (mortgage.prva_analyza_registracia_dt) {
        const date = new Date(mortgage.prva_analyza_registracia_dt);
        this.checkPoints++;
        this.progressBarData.push({
          label: "registrácia",
          data:`${date.getUTCDate()}.${date.getUTCMonth() + 1}.${date.getUTCFullYear()}`,
        });
      } else {
        this.progressBarData.push({
          label: "registrácia",
          data: null,
        });
      }
    },
    calculateDti() {
      return 158000;
    },
    mapIncomeType(incomes) {
      const incomesStored = [...(incomes || [])].sort((a, b) => new Date(a.created) - new Date(b.created));
      return incomesStored.length ? this.translateIncomeType(incomesStored[0].druh) : "príjem";
    },
    getMortgagePurpose () {
      let maxAmount = Number.MIN_SAFE_INTEGER;
      const mortgage = this.firstAnalysisMortgage;
      if (!mortgage) return "nadobudnutie";
      let purpose = null;
      const amounts = {
        suma_nadobudnutie: mortgage.suma_nadobudnutie,
        suma_refinancovanie: mortgage.suma_refinancovanie,
        suma_bezucel: mortgage.suma_bezucel,
      };
      for (const [key, amount] of Object.entries(amounts)) {
        if (amount < maxAmount) continue;
        maxAmount = amount;
        purpose = key;
      }
      if (maxAmount === 0) {
        purpose = mortgage.ciel_prva_analyza;
      }
      switch (purpose) {
        case "suma_bezucel":
        case "americka_hypo":
          purpose = "bezucel";
          break;
        case "suma_refinancovanie":
        case "refinancovanie":
        case "konsolidacia":
          purpose = "refinancovanie";
          break;
        case "suma_nadobudnutie":
        case "nadobudnutie":
        case "uverovy_certifikat":
          purpose = "nadobudnutie";
          break;
        default:
          purpose = "nadobudnutie";
          break;
      }
      return purpose;
    },
    openClientChatPopUp () {
      this.clientData.sent_unread_messages = 0;
      this.$store.commit("popupManagerState/pushToPopupOrder", {
        type: null,
        payload: this.client,
        component: "ClientChatPopUp",
        rules: null,
      });
    },
    getDate (created) {
      const date = new Date(created.sent_latest_message_dt);
      return date.getDate();
    },
    getHours (created) {
      const date = new Date(created.sent_latest_message_dt);
      return date.getHours();
    },
    getMinutes (created) {
      const date = new Date(created.sent_latest_message_dt);
      if (date.getMinutes() < 10) {
        return "0" + date.getMinutes();
      }
      return date.getMinutes();
    },
    getMonth (created) {
      const date = new Date(created.sent_latest_message_dt);
      return date.getMonth() + 1;
    },
    getYears (created) {
      const date = new Date(created.sent_latest_message_dt);
      return date.getFullYear();
    },
    getDay (created) {
      const date = new Date(created.sent_latest_message_dt);
      return date.getDay();
    },
  },
  watch: {
    async refreshGetter(refresh) {
      if (!this.search || !refresh) return;
      this.clientData = await getClientById(this.clientData.id);
    },
    client (newClient) {
      const deepEqual = require("deep-equal");
      if (deepEqual(this.clientData, newClient, true)) return;
      this.clientData = newClient;
      this.checkProgressBar();
    },
    open: function () {
      let routeData = this.$router.resolve({
        name: "ChatContent",
        params: {id: this.client.id},
      });
      window.open(
        routeData.href,
        "_blank",
        "height=818,width=515"
      );
    },
  },
};
</script>

<style scoped lang="scss">
.klient-item {
  padding: 14px 18px;
}

.klient-item-to-part {
  min-height: 76px;
  grid-template-columns: minmax(230px, 0.9fr) minmax(260px, 1fr) minmax(180px, 0.65fr);
  gap: 22px;
}

.custom-client .klient-item-to-part {
  grid-template-columns: minmax(230px, 0.9fr) minmax(160px, 0.65fr) minmax(360px, 1.35fr);
}

.admin-client-item .klient-item-to-part,
.admin-client-item.custom-client .klient-item-to-part {
  grid-template-columns: minmax(280px, 1fr) minmax(230px, 0.9fr) minmax(220px, 0.8fr);
}

.first-analysis-identity {
  min-width: 0;
}

.first-analysis-identity-row {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
}

.client-copy {
  min-width: 0;
  flex: 1;
}

.assigned-broker,
.client-copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 4px;
}

.assigned-broker {
  flex: 0 0 auto;
}

.identity-kicker {
  color: var(--app-text-muted);
  font-size: 8px;
  font-weight: 700;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.assigned-broker-avatar {
  display: grid;
  width: 44px;
  height: 44px;
  place-items: center;
  border: 1px solid var(--app-border);
  border-radius: 13px;
  background: var(--app-surface-muted);
}

::v-deep .assigned-broker-avatar .klienticta {
  margin: 0;
}

::v-deep .assigned-broker-avatar .broker-image {
  width: 36px;
  height: 36px;
  border: 2px solid var(--app-surface);
  object-fit: cover;
}

.first-analysis-progress {
  display: grid;
  min-width: 0;
  grid-template-columns: minmax(130px, 1fr) auto;
  align-items: center;
  gap: 8px 12px;
}

.first-analysis-progress-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.first-analysis-progress-copy span,
.first-analysis-step {
  color: var(--app-text-muted);
  font-size: 9px;
  font-weight: 600;
}

.first-analysis-progress-copy strong {
  overflow: hidden;
  color: var(--app-text);
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.first-analysis-step {
  grid-column: 2;
  grid-row: 1;
  align-self: end;
  white-space: nowrap;
}

.first-analysis-bars {
  display: flex;
  grid-column: 1 / -1;
  gap: 8px;
}

.first-analysis-bars span {
  height: 4px;
  flex: 1;
  border-radius: 999px;
  background: var(--app-surface-strong);
  opacity: 0.65;
}

.first-analysis-bars span.is-complete {
  background: var(--app-primary);
  opacity: 1;
}

.first-analysis-actions {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.custom-client-status {
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 2px;
}

.custom-client-status span,
.custom-client-status small,
.custom-client-action-copy span {
  color: var(--app-text-muted);
  font-size: 9px;
  font-weight: 600;
  line-height: 1.3;
}

.custom-client-status strong,
.custom-client-action-copy strong {
  overflow: hidden;
  color: var(--app-text);
  font-size: 11px;
  font-weight: 800;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.custom-client-status small {
  display: inline-flex;
  margin-top: 4px;
  padding: 3px 7px;
  border: 1px solid var(--app-border);
  border-radius: 999px;
  background: var(--app-surface-muted);
}

.first-analysis-actions.custom-client-actions {
  min-height: 54px;
  padding: 8px 10px 8px 13px;
  justify-content: space-between;
  border: 1px solid var(--app-border);
  border-radius: 12px;
  background: var(--app-surface-muted);
  color: var(--app-text);
}

.custom-client-action-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.custom-client-action-buttons {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.first-analysis-action {
  min-width: 0;
}

::v-deep .custom-client-actions .klienticta {
  display: flex;
  min-width: 66px;
  margin: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--app-primary);
  font-size: 10px;
  font-weight: 700;
  text-align: center;
}

::v-deep .custom-client-actions .nav-link-icon {
  width: 23px;
  height: 23px;
  margin-bottom: 2px;
}

.client-badges {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.client-badges:empty {
  display: none;
}

.source-badge {
  display: inline-flex;
  margin: 0;
  padding: 2px 8px;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 6px;
  text-decoration: none;
  transition: opacity 0.2s ease, transform 0.2s ease;

  &::before {
    width: 6px;
    height: 6px;
    flex: 0 0 6px;
    border-radius: 50%;
    background: currentColor;
    content: "";
  }

  &:hover {
    opacity: 0.85;
    transform: translateY(-1px);
  }

  // Farebné schémy pre jednotlivé kategórie
  &.source-organika {
    background: var(--app-primary-soft);
    color: var(--app-primary);
  }

  &.source-ads {
    background: var(--app-primary-soft);
    color: var(--app-primary);
  }

  &.source-dev {
    background: var(--app-surface-muted);
    color: var(--app-text-muted);
    text-transform: none;
  }

  &.source-default {
    background: var(--app-surface-muted);
    color: var(--app-text-muted);
  }
}

@media screen and (max-width: 1050px) {
  .klient-item-to-part,
  .admin-client-item .klient-item-to-part {
    grid-template-columns: minmax(220px, 1fr) minmax(240px, 1fr);
  }

  .custom-client .klient-item-to-part,
  .admin-client-item.custom-client .klient-item-to-part {
    grid-template-columns: minmax(220px, 1fr) minmax(180px, 0.75fr);
  }

  .first-analysis-actions {
    grid-column: 1 / -1;
    justify-content: flex-start;
  }
}

@media screen and (max-width: 767px) {
  .klient-item-to-part,
  .custom-client .klient-item-to-part,
  .admin-client-item .klient-item-to-part,
  .admin-client-item.custom-client .klient-item-to-part {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .first-analysis-actions {
    grid-column: auto;
  }

  .first-analysis-actions.custom-client-actions {
    align-items: flex-start;
  }
}
</style>
